import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { fetchApi } from '@/common/lib/fetchApi'
import { Text } from '@/common/components/text'
import { Stack } from '@/common/components/stack'
import { colors } from '@/core/styles/tokens'

type Role = 'employee' | 'manager'

interface EmployeeResult {
  email: string
  status: 'created' | 'skipped' | 'not_found'
  role: Role
  first_name: string | null
  last_name: string | null
  department: string | null
  job_title: string | null
}

interface ImportResponse {
  created: number
  skipped: number
  not_found: number
  managers_resolved: number
  results: EmployeeResult[]
}

interface ImportRequest {
  emails: string[]
  role_overrides?: Record<string, Role>
}

function parseEmails(text: string): string[] {
  return text
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && s.includes('@'))
}

function statusColor(status: string) {
  if (status === 'created') return colors.success
  if (status === 'skipped') return colors.gray70
  return colors.error
}

function ImportPeoplePage() {
  const [rawText, setRawText] = useState('')
  const [preview, setPreview] = useState<ImportResponse | null>(null)
  const [importResult, setImportResult] = useState<ImportResponse | null>(null)
  // Per-row role overrides keyed by lowercased email. Only used during preview;
  // sent on commit so the response role mirrors what the admin saw.
  const [roleOverrides, setRoleOverrides] = useState<Record<string, Role>>({})

  const emails = parseEmails(rawText)

  const previewMutation = useMutation({
    mutationFn: (body: ImportRequest) =>
      fetchApi<ImportResponse>('/api/org/import-people/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      setPreview(data)
      setImportResult(null)
    },
  })

  const importMutation = useMutation({
    mutationFn: (body: ImportRequest) =>
      fetchApi<ImportResponse>('/api/org/import-people', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      setImportResult(data)
      setPreview(null)
    },
  })

  const handlePreview = () => {
    if (emails.length === 0) return
    const overrides = Object.keys(roleOverrides).length > 0 ? roleOverrides : undefined
    previewMutation.mutate({ emails, role_overrides: overrides })
  }

  const handleImport = () => {
    if (emails.length === 0) return
    const overrides = Object.keys(roleOverrides).length > 0 ? roleOverrides : undefined
    importMutation.mutate({ emails, role_overrides: overrides })
  }

  const handleRoleChange = (email: string, role: Role) => {
    const key = email.trim().toLowerCase()
    setRoleOverrides((prev) => ({ ...prev, [key]: role }))
  }

  const canPreview = emails.length > 0

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200 }}>
      <Stack gap="16">
        <Text size="xl" weight="bold">
          Import People
        </Text>
        <Text size="sm" style={{ color: colors.gray70 }}>
          Paste employee and manager emails (one per line, or comma/semicolon separated). Each email
          is validated against BambooHR. The role column is inferred from BambooHR job title and can
          be overridden per row before committing.
        </Text>
        <Text size="sm" style={{ color: colors.gray70 }}>
          Note: Real authorization (whether a person can approve timesheets) comes from their Okta
          group membership on login, not from what you select here. The role column is for your
          confirmation only.
        </Text>

        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, maxWidth: 500 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
              Email addresses
            </label>
            <textarea
              value={rawText}
              onChange={(e) => {
                setRawText(e.target.value)
                setPreview(null)
                setImportResult(null)
              }}
              placeholder={'jane.doe@orchard.com  (employee)\nbob.manager@orchard.com  (manager)'}
              rows={12}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: `1px solid ${colors.gray20}`,
                fontSize: 13,
                fontFamily: 'monospace',
                resize: 'vertical',
              }}
            />
            <div style={{ marginTop: 4, fontSize: 13, color: colors.gray70 }}>
              {emails.length} email{emails.length !== 1 ? 's' : ''} detected
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button
                onClick={handlePreview}
                disabled={!canPreview || previewMutation.isPending}
                style={{
                  padding: '8px 20px',
                  borderRadius: 6,
                  border: 'none',
                  background: canPreview ? colors.sea : colors.gray50,
                  color: colors.white,
                  fontSize: 14,
                  cursor: canPreview ? 'pointer' : 'default',
                }}
              >
                {previewMutation.isPending ? 'Checking BambooHR...' : 'Preview'}
              </button>

              {preview && preview.not_found === 0 && preview.created > 0 && (
                <button
                  onClick={handleImport}
                  disabled={importMutation.isPending}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 6,
                    border: 'none',
                    background: colors.success,
                    color: colors.white,
                    fontSize: 14,
                    cursor: 'pointer',
                  }}
                >
                  {importMutation.isPending
                    ? 'Importing...'
                    : `Import ${preview.created} ${preview.created !== 1 ? 'people' : 'person'}`}
                </button>
              )}
            </div>

            {previewMutation.isError && (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  background: colors.errorBackground,
                  border: `1px solid ${colors.errorOutline}`,
                  borderRadius: 6,
                  color: colors.error,
                  fontSize: 14,
                }}
              >
                {(previewMutation.error as Error).message}
              </div>
            )}

            {importMutation.isError && (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  background: colors.errorBackground,
                  border: `1px solid ${colors.errorOutline}`,
                  borderRadius: 6,
                  color: colors.error,
                  fontSize: 14,
                }}
              >
                {(importMutation.error as Error).message}
              </div>
            )}

            {importResult && (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  background: colors.successBackground,
                  border: `1px solid ${colors.successOutline}`,
                  borderRadius: 6,
                  color: colors.success,
                  fontSize: 14,
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  Imported {importResult.created}{' '}
                  {importResult.created !== 1 ? 'people' : 'person'}
                  {importResult.managers_resolved > 0 &&
                    `, ${importResult.managers_resolved} manager relationship${importResult.managers_resolved !== 1 ? 's' : ''} resolved`}
                  {importResult.skipped > 0 && `, ${importResult.skipped} skipped`}
                </div>
              </div>
            )}
          </div>

          {/* Results table */}
          {(preview || importResult) && (
            <div style={{ flex: 1 }}>
              <Text size="lg" weight="bold">
                {preview ? 'Preview' : 'Results'}
              </Text>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  marginTop: 8,
                  fontSize: 14,
                }}
              >
                <thead>
                  <tr style={{ borderBottom: `2px solid ${colors.gray20}` }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>Department</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>Role</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview ?? importResult)!.results.map((r) => (
                    <tr key={r.email} style={{ borderBottom: `1px solid ${colors.gray10}` }}>
                      <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: 13 }}>
                        {r.email}
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        {r.first_name && r.last_name ? `${r.first_name} ${r.last_name}` : '—'}
                      </td>
                      <td style={{ padding: '4px 8px' }}>{r.department ?? '—'}</td>
                      <td style={{ padding: '4px 8px' }}>
                        {preview ? (
                          <select
                            value={r.role}
                            onChange={(e) => handleRoleChange(r.email, e.target.value as Role)}
                            disabled={r.status === 'not_found'}
                            style={{
                              padding: '2px 6px',
                              borderRadius: 4,
                              border: `1px solid ${colors.gray20}`,
                              fontSize: 13,
                              fontWeight: r.role === 'manager' ? 600 : 400,
                              background: r.role === 'manager' ? colors.infoBackground : colors.white,
                            }}
                          >
                            <option value="employee">employee</option>
                            <option value="manager">manager</option>
                          </select>
                        ) : (
                          <span
                            style={{
                              fontWeight: r.role === 'manager' ? 600 : 400,
                              color: r.role === 'manager' ? colors.info : colors.martinique,
                            }}
                          >
                            {r.role}
                          </span>
                        )}
                      </td>
                      <td
                        style={{
                          padding: '4px 8px',
                          color: statusColor(r.status),
                          fontWeight: 500,
                        }}
                      >
                        {r.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {preview && preview.not_found > 0 && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    background: colors.errorBackground,
                    border: `1px solid ${colors.errorOutline}`,
                    borderRadius: 6,
                    color: colors.error,
                    fontSize: 13,
                  }}
                >
                  {preview.not_found} email{preview.not_found !== 1 ? 's' : ''} not found in
                  BambooHR. Remove them before importing.
                </div>
              )}
            </div>
          )}
        </div>
      </Stack>
    </div>
  )
}

export const Route = createFileRoute('/org/import-people')({
  component: ImportPeoplePage,
})
